
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';

export interface LocationSelection {
  locationId: number;
  locationName: string;
}

@Component({
  name: 'DeliveryLocationDialog'
})
export default class DeliveryLocationDialog extends Vue {
  @Prop({ type: Boolean, default: false }) value!: boolean;
  @Prop({ type: Function, default: null }) onConfirm!: ((selection: LocationSelection) => Promise<void>) | null;

  private show = false;
  private selectedLocationId: number | null = null;
  private locations: StoreLocationModel[] = [];
  private loadingLocations = false;
  private confirming = false;
  private errorMessage = '';
  private loadError: string | null = null;

  get locationOptions() {
    return this.locations.map(location => ({
      id: location.id,
      name: location.name
    }));
  }

  /** Check if locations loaded successfully but none exist */
  get hasNoLocations(): boolean {
    return !this.loadingLocations && !this.loadError && this.locations.length === 0;
  }

  /** Check if a location has been selected (explicit null check to allow id=0) */
  get hasSelectedLocation(): boolean {
    return this.selectedLocationId !== null && this.selectedLocationId !== undefined;
  }

  /** Validation rule for delivery location selection */
  validateDeliveryLocation(v: number | null): true | string {
    return (v !== null && v !== undefined) || 'Delivery location is required';
  }

  @Watch('value')
  onValueChange(val: boolean): void {
    this.show = val;
  }

  @Watch('show')
  onShowChange(val: boolean): void {
    this.$emit('input', val);
    if (val) {
      this.fetchLocations();
    } else {
      this.reset();
    }
  }

  private async fetchLocations(): Promise<void> {
    // Prevent concurrent fetches when dialog toggles rapidly
    if (this.loadingLocations) return;

    this.loadingLocations = true;
    this.loadError = null;
    try {
      const storeLocationList = new StoreLocationList();
      await storeLocationList.fetch();
      this.locations = storeLocationList.items;

      // If there's only one location, pre-select it
      if (this.locations.length === 1) {
        this.selectedLocationId = this.locations[0].id;
      }
    } catch (error) {
      console.error('[DeliveryLocationDialog] Failed to load locations:', error);
      this.loadError = 'Failed to load store locations. Please try again.';
    } finally {
      this.loadingLocations = false;
    }
  }

  private async confirm(): Promise<void> {
    if (this.selectedLocationId === null || this.selectedLocationId === undefined) {
      this.errorMessage = 'Please select a delivery location';
      return;
    }

    const selectedLocation = this.locations.find(l => l.id === this.selectedLocationId);
    if (!selectedLocation) {
      this.errorMessage = 'Selected location not found';
      return;
    }

    const selection: LocationSelection = {
      locationId: this.selectedLocationId,
      locationName: selectedLocation.name
    };

    if (this.onConfirm) {
      this.confirming = true;
      try {
        await this.onConfirm(selection);
        this.show = false;
      } catch (error: unknown) {
        console.error('[DeliveryLocationDialog] Confirm failed:', error);
        if (error instanceof Error) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = String(error) || 'Operation failed';
        }
      } finally {
        this.confirming = false;
      }
    } else {
      this.$emit('confirm', selection);
      this.show = false;
    }
  }

  private cancel(): void {
    this.$emit('cancel');
    this.show = false;
  }

  private reset(): void {
    this.selectedLocationId = null;
    this.errorMessage = '';
    this.loadError = null;
    this.confirming = false;
  }
}
